import React, { useState } from "react";
import { Link, useLocation } from "react-router-dom";
import {
  Home,
  Info,
  Phone,
  PhoneCall,
  Mail,
  MessageCircle,
  X,
  Menu,
} from "lucide-react";

/* ⭐ Reusable Contact Card Component */
function ContactCard({ title, phone, email, whatsapp }) {
  return (
    <div className="mt-6 p-4 rounded-xl border border-[#E63946]/40 bg-black/30 backdrop-blur-lg shadow">
      <h3 className="text-[#E63946] font-semibold text-lg mb-3">{title}</h3>

      <div className="space-y-3 text-gray-300 text-sm">
        <div className="flex items-center space-x-3">
          <PhoneCall className="w-5 h-5 text-[#E63946]" />
          <a href={`tel:${phone}`} className="hover:text-white">
            {phone}
          </a>
        </div>

        <div className="flex items-center space-x-3">
          <Mail className="w-5 h-5 text-[#E63946]" />
          <a href={`mailto:${email}`} className="hover:text-white">
            {email}
          </a>
        </div>

        <div className="flex items-center space-x-3">
          <MessageCircle className="w-5 h-5 text-[#25D366]" />
          <a
            href={`https://wa.me/${whatsapp}`}
            target="_blank"
            rel="noopener noreferrer"
            className="hover:text-white"
          >
            WhatsApp: {whatsapp}
          </a>
        </div>
      </div>
    </div>
  );
}

export default function Header() {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const location = useLocation();

  const isActive = (path) => location.pathname === path;

  return (
    <>
      {/* ⭐ FIXED WHITE TOP NAVBAR */}
      <nav className="fixed top-0 left-0 right-0 z-20 bg-white border-b border-gray-200 shadow-sm">
        <div className="max-w-[1400px] mx-auto px-4 sm:px-8 py-4 flex items-center justify-between">
          <Link to="/">
            <img
              src="/Kharbanda Motors logo (3)-10.png"
              alt="KM Motors"
              className="h-10 transform transition duration-300 hover:scale-105 cursor-pointer"
            />
          </Link>

          <button
            onClick={() => setIsMenuOpen(true)}
            className="p-2 rounded-md border border-gray-300 hover:border-[#E63946] transition bg-white"
          >
            <Menu className="w-7 h-7 text-gray-700" />
          </button>
        </div>
      </nav>

      {/* ⭐ BACKDROP */}
      {isMenuOpen && (
        <div
          onClick={() => setIsMenuOpen(false)}
          className="fixed inset-0 bg-black/50 backdrop-blur-sm z-[900]"
        ></div>
      )}

      {/* ⭐ SPORTY RED + BLACK DRAWER (Improved layout) */}
      <aside
        className={`fixed top-0 right-0 h-full w-80 max-w-[85%] z-[1001]
        bg-gradient-to-b from-[#0B0B0C] to-[#1A1A1C]
        backdrop-blur-xl border-l border-[#E63946]/40
        transform transition-transform duration-300
        ${
          isMenuOpen
            ? "translate-x-0 shadow-[0_0_25px_rgba(230,57,70,0.3)]"
            : "translate-x-full"
        }
        `}
      >
        {/* Scrollable content to prevent cropping */}
        <div className="p-4 overflow-y-auto h-full">
          {/* FIRST ROW — Home + Close Button */}
          <div className="flex items-center justify-between">
            {/* HOME LINK ON LEFT */}
            <Link
              to="/"
              onClick={() => setIsMenuOpen(false)}
              className="flex items-center space-x-3 group"
            >
              <div
                className={`p-2 rounded-full border transition-all duration-300 ${
                  isActive("/")
                    ? "border-[#E63946] bg-[#E63946]/10"
                    : "border-gray-300 group-hover:border-[#E63946]"
                }`}
              >
                <Home
                  size={12}
                  className={`transition ${
                    isActive("/")
                      ? "text-[#E63946]"
                      : "text-gray-300 group-hover:text-[#E63946]"
                  }`}
                />
              </div>

              <span
                className={`text-lg tracking-wide ${
                  isActive("/")
                    ? "text-[#E63946] font-semibold"
                    : "text-gray-300 group-hover:text-[#E63946]"
                }`}
              >
                Home
              </span>
            </Link>

            {/* X BUTTON ON RIGHT */}
            <button
              onClick={() => setIsMenuOpen(false)}
              className="p-2 rounded-full  hover:border-[#E63946] transition"
            >
              <X className="w-4 h-4 text-gray-300" />
            </button>
          </div>
          {/* SPACING AFTER FIRST ROW */}
          <div className="mt-6" />
          {/* REST OF LINKS */}
          <nav className="space-y-5">
            {/* ABOUT */}
            <Link
              to="/about-us"
              onClick={() => setIsMenuOpen(false)}
              className="flex items-center space-x-3 group"
            >
              <div
                className={`p-2 rounded-full border transition ${
                  isActive("/about-us")
                    ? "border-[#E63946] bg-[#E63946]/10"
                    : "border-gray-300 group-hover:border-[#E63946]"
                }`}
              >
                <Info
                  size={12}
                  className={`transition ${
                    isActive("/about-us")
                      ? "text-[#E63946]"
                      : "text-gray-300 group-hover:text-[#E63946]"
                  }`}
                />
              </div>

              <span
                className={`text-lg tracking-wide ${
                  isActive("/about-us")
                    ? "text-[#E63946] font-semibold"
                    : "text-gray-300 group-hover:text-[#E63946]"
                }`}
              >
                About
              </span>
            </Link>

            {/* CONTACT */}
            <Link
              to="/contact"
              onClick={() => setIsMenuOpen(false)}
              className="flex items-center space-x-3 group"
            >
              <div
                className={`p-2 rounded-full border transition ${
                  isActive("/contact")
                    ? "border-[#E63946] bg-[#E63946]/10"
                    : "border-gray-300 group-hover:border-[#E63946]"
                }`}
              >
                <Phone
                  size={12}
                  className={`transition ${
                    isActive("/contact")
                      ? "text-[#E63946]"
                      : "text-gray-300 group-hover:text-[#E63946]"
                  }`}
                />
              </div>

              <span
                className={`text-lg tracking-wide ${
                  isActive("/contact")
                    ? "text-[#E63946] font-semibold"
                    : "text-gray-300 group-hover:text-[#E63946]"
                }`}
              >
                Contact
              </span>
            </Link>
          </nav>
          {/* CONTACT CARDS — SAME AS BEFORE */}
          <ContactCard
            title="Kharbanda Motors"
            phone="+91 7266929245"
            email="contact@kharbandamotors.com"
            whatsapp="917266929245"
          />
          <ContactCard
            title="Hyundai Spare Parts"
            phone="+91 7408740651"
            email="contact@kharbandamotors.com"
            whatsapp="917408740651"
          />
          <ContactCard
            title="Mahindra Spare Parts"
            phone="+91 7408740652"
            email="contact@kharbandamotors.com"
            whatsapp="917408740652"
          />
        </div>
      </aside>
    </>
  );
}
